import { LitElement, html, css, nothing } from 'lit';
import { StoreMixin } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { count, mediumDate, money, since } from '../lib/format.js';
import { quarterHours, staleSentence } from '../lib/deal.js';

/**
 * The Deals screen (Estimate Tool 13.1; Phase E17): every open HubSpot deal, best-funded first,
 * with what its estimate says and whether it still holds.
 *
 * Three decisions the design makes and this screen keeps:
 *
 * **The deal is the row, not the estimate.** A deal with no estimate is on the list, because the
 * question a planner brings here is "what would this take", and a list of only the answered ones
 * would hide the deals that most need asking.
 *
 * **Stale is said, never hidden.** An estimate generated before the deal's amount moved or a
 * note landed is still shown — it is what was said — with a word beside it and the reasons on
 * the deal's own page. A sync with regeneration on refreshes it; one with it off marks it.
 *
 * **Nothing here writes.** Generating and the kick-off date live on the deal's page, where the
 * evidence is; a table that could regenerate a row could regenerate the wrong one.
 */
class EstimateDealList extends StoreMixin(LitElement) {
  static properties = {
    _data: { state: true },
    _error: { state: true },
    _showClosed: { state: true },
  };

  constructor() {
    super();
    this._data = null;
    this._error = null;
    this._showClosed = false;
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 24px 18px;
    }
    .eyebrow {
      font-family: var(--font-eyebrow);
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: var(--ink-muted);
    }
    h2 {
      margin: 6px 0 4px;
      font-size: 20px;
      color: var(--ink-strong);
    }
    .lede {
      margin: 0 0 16px;
      color: var(--ink-muted);
      font-size: 13.5px;
      max-width: 72ch;
      line-height: 1.5;
    }
    .controls {
      display: flex;
      align-items: center;
      gap: 8px 16px;
      flex-wrap: wrap;
      margin-bottom: 12px;
    }
    .toggle {
      font: inherit;
      font-size: 12.5px;
      font-weight: 600;
      padding: 4px 12px;
      border: 1px solid var(--control-border);
      border-radius: var(--radius-pill);
      background: var(--control-bg);
      color: var(--ink-strong);
      cursor: pointer;
    }
    .shown {
      font-size: 12.5px;
      color: var(--ink-muted);
    }
    .scroll {
      overflow-x: auto;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      font-size: 13px;
    }
    th,
    td {
      text-align: left;
      padding: 8px 10px;
      border-bottom: 1px solid var(--border-soft);
      white-space: nowrap;
      vertical-align: top;
    }
    th {
      font-size: 11.5px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--ink-muted);
      font-weight: 700;
    }
    td.num,
    th.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
    tbody tr:hover {
      background: var(--surface-2);
    }
    tr.closed td {
      opacity: 0.6;
    }
    a.row {
      color: var(--ink-strong);
      font-weight: 600;
      text-decoration: none;
    }
    a.row:hover {
      text-decoration: underline;
    }
    .client {
      color: var(--ink);
    }
    .muted {
      color: var(--ink-muted);
      font-size: 12px;
    }
    .pill {
      display: inline-block;
      margin-left: 6px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      border: 1px solid var(--border);
      border-radius: var(--radius-pill);
      padding: 0 6px;
      color: var(--ink-muted);
    }
    .pill.stale {
      color: var(--status-caution-ink, var(--ink-strong));
      background: var(--status-caution);
      border-color: transparent;
    }
    .pill.type {
      text-transform: none;
      letter-spacing: 0;
    }
    .empty,
    .state,
    .error {
      padding: 16px 0;
      color: var(--ink-muted);
      max-width: 62ch;
      line-height: 1.55;
    }
    .error {
      color: var(--status-overloaded);
      font-weight: 600;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    try {
      this._data = await api.get('/estimate/deals', this._showClosed ? { closed: true } : {});
      this._error = null;
    } catch (err) {
      this._error = `Could not load the deals: ${err.message}`;
    }
  }

  _go(event, path) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    navigate(path);
  }

  async _toggleClosed() {
    this._showClosed = !this._showClosed;
    await this._load();
  }

  _estimateCell(d) {
    const e = d.estimate;
    if (!e) return html`<span class="muted">Not yet</span>`;
    return html`
      ${e.total_hours === null ? '—' : quarterHours(e.total_hours)}
      ${e.inferred_type ? html`<span class="pill type">${e.inferred_type.replace(/_/g, ' ')}</span>` : nothing}
      ${
        e.stale
          ? html`<span class="pill stale" title=${staleSentence(e.stale_reasons)}>stale</span>`
          : nothing
      }
      <div class="muted">v${e.version} · ${since(e.generated_at)}</div>
    `;
  }

  _row(d) {
    const path = `/estimate/deals/${d.id}`;
    return html`
      <tr class=${d.status === 'open' ? '' : 'closed'}>
        <td class="client">${d.client ?? html`<span class="muted">No company</span>`}</td>
        <td>
          <a class="row" href=${path} @click=${(ev) => this._go(ev, path)}>${d.name}</a>
          ${d.status !== 'open' ? html`<span class="pill">${d.status}</span>` : nothing}
        </td>
        <td class="num">${d.amount === null ? '—' : money(d.amount)}</td>
        <td>${d.stage_name ?? d.stage_id}</td>
        <td>${d.expected_close_date ? mediumDate(d.expected_close_date) : '—'}</td>
        <td class="num">
          ${d.activity.count ? count(d.activity.count) : '—'}
          ${d.activity.last_at ? html`<div class="muted">${since(d.activity.last_at)}</div>` : nothing}
        </td>
        <td>${this._estimateCell(d)}</td>
        <td>
          ${
            d.kickoff
              ? mediumDate(d.kickoff.date)
              : d.estimate?.kickoff_date
                ? html`<span class="muted">${mediumDate(d.estimate.kickoff_date)} (derived)</span>`
                : '—'
          }
        </td>
      </tr>
    `;
  }

  render() {
    if (this._error && !this._data)
      return html`<div class="card"><p class="error">${this._error}</p></div>`;
    if (!this._data) return html`<div class="card"><p class="state">Loading…</p></div>`;
    const { deals, counts } = this._data;
    return html`
      <div class="card">
        <div class="eyebrow">Deals</div>
        <h2>What each open deal would take</h2>
        <p class="lede">
          Every open HubSpot deal, best-funded first. An estimate is worked out from the deal's
          amount, its title and the notes, emails, calls and meetings logged against it, and the
          closed projects it most resembles — and regenerated as the deal moves. HubSpot has no
          kick-off date, so the one the forecast uses is set here.
        </p>
        ${this._error ? html`<p class="error">${this._error}</p>` : nothing}
        <div class="controls">
          <button class="toggle" @click=${() => this._toggleClosed()}>
            ${this._showClosed ? 'Open deals only' : 'Show closed deals'}
          </button>
          <span class="shown">
            ${count(counts.open)} open · ${count(counts.estimated)} estimated
            ${counts.stale ? html`· ${count(counts.stale)} stale` : nothing} ·
            ${count(counts.with_kickoff)} with a kick-off date
          </span>
        </div>
        ${
          deals.length === 0
            ? html`<p class="empty">
                No deals to show. A HubSpot sync fills this list with the open deals of the read
                pipelines.
              </p>`
            : html`
                <div class="scroll">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Company</th>
                        <th scope="col">Deal</th>
                        <th scope="col" class="num">Amount</th>
                        <th scope="col">Stage</th>
                        <th scope="col">Close</th>
                        <th scope="col" class="num">Activity</th>
                        <th scope="col">Estimate</th>
                        <th scope="col">Kick-off</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${deals.map((d) => this._row(d))}
                    </tbody>
                  </table>
                </div>
              `
        }
      </div>
    `;
  }
}

customElements.define('estimate-deal-list', EstimateDealList);
